import React, { useState, useEffect, useRef } from "react";
import axios, { AxiosRequestConfig } from "axios";
import styled from "styled-components/macro";
import { spacing, SpacingProps } from "@mui/system";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  CircularProgress,
  Box as MuiBox,
  Typography as MuiTypography,
  Button as MuiButton,
  IconButton
} from "@mui/material";
import CardImageRatio from "../../components/logoComponents/CardImageRatio";
import UploadLogo from "../../components/logoComponents/UploadLogo";
import Loader from "../../components/loader/Loader";
import { Papercontainer } from "@rewardle/apps.ui.papercontainer.papercontainer";
import { scrollToTop } from "../../utils/commonFunctions";
import CompressedFile from "../../utils/imageCompression";
import UploadFile from "../../utils/uploadFile";

const Box = styled(MuiBox)`
  display: flex;
  gap: 28px;
  margin-top: 32px;
`;

const HeaderBox = styled(MuiBox)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
  margin-right: 16px;
  cursor: pointer;
`;

interface ButtonProps extends SpacingProps {
  component?: React.ElementType;
  to?: string;
  target?: string;
}

const Button = styled(MuiButton)<ButtonProps>({
  height: "43px",
  "&.custom_disabled": {
    backgroundColor: "grey !important"
  }
});

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

type BrandingPageProps = {
  businessId: string;
};

const s3BucketName: any = process.env.REACT_APP_BUCKET_NAME;

const BrandingPage: React.FC<BrandingPageProps> = ({ businessId }) => {
  const accessToken: any = localStorage?.getItem("accessToken");
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: ""
  });

  const [configDetailLogo, setConfigDetailLogo] = useState<any>({
    Logo: "",
    BusinessName: "",
    Design: {
      Color: {
        Primary: "",
        Secondary: "",
        Tertiary: ""
      }
    },
    BrandConfig: {
      faq_url: "",
      tnc_url: ""
    },
    BusinessInfo: {
      Logo: "",
      file: null
    }
  });

  const childFunctionRef = useRef<any>(null);

  useEffect(() => {
    getBrandingList();
    childFunctionRef.current = getBrandingList;
  }, [businessId]);

  const getBrandingList = async () => {
    setLoading(true);
    try {
      const config: AxiosRequestConfig = {
        method: "get",
        url: `${apiBaseUrl}/datacollectionbusiness/business/branddetails/${businessId}/rewardlepay/public`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${accessToken}`
        }
      };
      const response = await axios(config);
      let brandConfig = response.data.BrandConfig;
      if (typeof brandConfig === "string") {
        try {
          brandConfig = JSON.parse(brandConfig);
        } catch (parseError) {
          console.error("Error parsing BrandConfig:", parseError);
          brandConfig = {};
        }
      }
      setConfigDetailLogo({
        ...response.data,
        BrandConfig: brandConfig,
        BusinessInfo: {
          Logo: response.data?.Logo,
          file: null
        }
      });

      // Clear any existing notifications on successful refresh
      setNotificationMessage({
        message: "",
        type: ""
      });
    } catch (error) {
      console.error("Error fetching branding data:", error);
      // Initialize with empty data if API fails
      setConfigDetailLogo({
        Logo: "",
        BusinessName: "",
        Design: {
          Color: {
            Primary: "",
            Secondary: "",
            Tertiary: ""
          }
        },
        BrandConfig: {
          faq_url: "",
          tnc_url: ""
        },
        BusinessInfo: {
          Logo: "",
          file: null
        }
      });

      setNotificationMessage({
        message: "Failed to load branding data. Please try refreshing.",
        type: "error"
      });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await getBrandingList();
  };

  const updateBrandingData = async () => {
    setIsSaveLoading(true);
    setIsSaveDisabled(true);

    try {
      let payload = { ...configDetailLogo };

      // Upload logo if it exists
      if (configDetailLogo?.file) {
        const logoCompressedImageFile = await CompressedFile(
          configDetailLogo.file
        );
        const logoS3Url = await UploadFile(
          logoCompressedImageFile,
          s3BucketName,
          `business/${businessId}/logo/${businessId}_logo_rewardlepay.jpg`,
          accessToken
        );
        payload.Logo = logoS3Url;
      }

      const config: AxiosRequestConfig = {
        method: "put",
        url: `${apiBaseUrl}/datacollectionbusiness/business/branddetails/${businessId}/rewardlepay`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${accessToken}`
        },
        data: payload
      };

      const response = await axios(config);

      setConfigDetailLogo({
        ...response.data,
        BrandConfig: JSON.parse(response?.data?.BrandConfig || "{}"),
        BusinessInfo: {
          Logo: response.data?.Logo,
          file: null
        }
      });

      getBrandingList();

      setNotificationMessage({
        message: "Business branding updated successfully",
        type: "success"
      });
    } catch (error: any) {
      const concatenatedMessage =
        error?.response?.data?.errors?.map((item: any) => item).join(" ") ||
        "An error occurred.";
      setNotificationMessage({
        message: concatenatedMessage,
        type: "error"
      });
      window.scroll({
        top: 0,
        left: 0,
        behavior: "smooth"
      });
    } finally {
      setIsSaveLoading(false);
      setIsSaveDisabled(false);
      scrollToTop();
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <React.Fragment>
        {notificationMessage?.message && (
          <div style={{ marginTop: "2px", marginBottom: "1px" }}>
            <Alert severity={notificationMessage?.type as any}>
              {notificationMessage?.message}
            </Alert>
          </div>
        )}
        <Papercontainer elevation={0}>
          <HeaderBox>
            <IconButton
              onClick={handleRefresh}
              disabled={isRefreshing}
              color="primary"
              aria-label="refresh"
            >
              {isRefreshing ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon sx={{ color: "#192f67" }} />
              )}
            </IconButton>
          </HeaderBox>

          {configDetailLogo ? (
            <>
              {configDetailLogo?.Logo ? (
                <Box
                  sx={{
                    position: "relative",
                    right: "80px"
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      position: "relative",
                      left: "5rem",
                      bottom: "3rem",
                      color: "rgba(34, 51, 84, 0.7)"
                    }}
                  >
                    Logo
                  </Typography>
                  <CardImageRatio
                    bgImage={configDetailLogo?.Logo}
                    maxWidth={80}
                    ratioDevide={4}
                    classes={"logoSizeForDetailsPage"}
                  />
                  <UploadLogo
                    setConfigDetailLogo={setConfigDetailLogo}
                    mode={<EditIcon />}
                    setIsSaveDisabled={setIsSaveDisabled}
                    configDetailLogo={configDetailLogo}
                  />
                </Box>
              ) : (
                <UploadLogo
                  setConfigDetailLogo={setConfigDetailLogo}
                  mode={"Add Logo"}
                  setIsSaveDisabled={setIsSaveDisabled}
                  configDetailLogo={configDetailLogo}
                />
              )}

              <Button
                variant="contained"
                color="secondary"
                style={{ marginTop: "10px" }}
                className={
                  isSaveDisabled ? "blue_button custom_disabled" : "blue_button"
                }
                disabled={isSaveDisabled}
                onClick={updateBrandingData}
              >
                {isSaveLoading ? (
                  <CircularProgress size={25} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </>
          ) : (
            <Typography variant="h5" mb={2} align="center">
              {"No Data Available"}
            </Typography>
          )}
        </Papercontainer>
      </React.Fragment>
    </>
  );
};

export default BrandingPage;
